import React from "react";
import { LoadingIndicator, ErrorMessage, Button, Text } from "@jsq/ds/core";
import { useGetAccountNameQuery } from "../../../../graphql/supergraph.index";
import { WorkflowType, CreatePaymentPrefEntryPoint, WorkflowMode } from "../../common/enums";
import DeletePaymentPrefModal from "../../Components/DeletePaymentPrefModal";
import { useDeletePaymentPref } from "../../Components/DeletePaymentPrefModal/hooks/use-delete-payment-pref";
import { ExistingPaymentPrefDisplayCard } from "../../Components/ExistingPaymentPrefDisplayCard";
import { PaymentPrefForPositionWorkflow } from "../../Workflows/PaymentPrefForPositionWorkflow";
import {
  CreateWorkflowParams,
  EditWorkflowParams,
} from "../../Workflows/PaymentPrefForPositionWorkflow/types";
import { positionPaymentPrefVar } from "./apollo-vars";
import { usePositionPaymentPref } from "./hooks/use-position-payment-pref";
import { PaymentPrefForPositionContainerProps } from "./types";

export const PaymentPrefForPositionContainer: React.FunctionComponent<
  PaymentPrefForPositionContainerProps
> = ({ accountId, entityId, entityName, positionId, paymentPrefId, arena }) => {
  const {
    showWorkflowComponent,
    workflowMode,
    workflowParams,
    paymentPref,
    canEdit,
    canReveal,
    loading,
    error,
    setShowWorkflowComponent,
    handlePaymentPrefCallbacks,
    paymentPrefId: displayPaymentPrefId,
  } = usePositionPaymentPref({
    accountId,
    entityId,
    entityName,
    positionId,
    paymentPrefId,
  });

  const {
    handleDeletePaymentInstructionClick,
    showDeletePaymentPrefModal,
    handleCancelDeletePaymentPref,
    deletePaymentPrefCallback,
  } = useDeletePaymentPref({
    workflowType: WorkflowType.Position,
    positionId,
    onSuccess: ({ workflowType }) => {
      if (workflowType === WorkflowType.Position) {
        // Update reactive variable to propagate query refresh
        positionPaymentPrefVar(null);
      }
    },
  });

  const { data: accountNameData, loading: accountNameLoading } = useGetAccountNameQuery({
    variables: { accountId },
    skip: !accountId || !arena?.features?.hasPositionPaymentPrefStaffApprovals,
  });

  if (loading || accountNameLoading) {
    return <LoadingIndicator variant="primary" />;
  }

  if (error) {
    return <ErrorMessage>Failed to load</ErrorMessage>;
  }

  return (
    <>
      {showWorkflowComponent && workflowMode && workflowParams && (
        <>
          {workflowMode === WorkflowMode.Create && (
            <PaymentPrefForPositionWorkflow
              onExit={() => setShowWorkflowComponent(false)}
              onSuccess={() => setShowWorkflowComponent(false)}
              workflowMode={WorkflowMode.Create}
              workflowParams={workflowParams as CreateWorkflowParams}
              accountName={accountNameData?.account?.name}
              arena={arena}
            />
          )}
          {workflowMode === WorkflowMode.Edit && (
            <PaymentPrefForPositionWorkflow
              onExit={() => setShowWorkflowComponent(false)}
              onSuccess={() => setShowWorkflowComponent(false)}
              workflowMode={WorkflowMode.Edit}
              workflowParams={workflowParams as EditWorkflowParams}
              accountName={accountNameData?.account?.name}
              arena={arena}
            />
          )}
        </>
      )}
      <DeletePaymentPrefModal
        show={showDeletePaymentPrefModal}
        onClose={handleCancelDeletePaymentPref}
        deleteCallback={deletePaymentPrefCallback}
      />
      <Text variant="h3" className="stacked-lg">
        Payment instructions
      </Text>
      {paymentPref ? (
        <ExistingPaymentPrefDisplayCard
          key={paymentPref.id}
          paymentPref={paymentPref}
          displayPositions
          displayRevealDetails={canReveal}
          displayInvalidInstructionsWarning
          callBacks={
            canEdit
              ? {
                  editPaymentInstructionsCallback: () =>
                    handlePaymentPrefCallbacks({ workflowMode: WorkflowMode.Edit }),
                  switchPaymentMethodCallback: () =>
                    handlePaymentPrefCallbacks({
                      workflowMode: WorkflowMode.Create,
                      createEntryPoint: CreatePaymentPrefEntryPoint.Switch,
                    }),
                  deletePaymentInstructionsCallback: () =>
                    displayPaymentPrefId &&
                    handleDeletePaymentInstructionClick(displayPaymentPrefId),
                }
              : undefined
          }
        />
      ) : (
        <div>
          <p>This position does not have any payment instructions configured</p>
          {canEdit && (
            <Button
              variant="neutral"
              isFullWidth
              onClick={() =>
                handlePaymentPrefCallbacks({
                  workflowMode: WorkflowMode.Create,
                  createEntryPoint: CreatePaymentPrefEntryPoint.Add,
                })
              }
            >
              Add payment instructions
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export type { PaymentPrefForPositionContainerProps };
